import { useState } from 'react';
import FadeIn from '@/components/FadeIn';
import { BUSINESS } from '@/lib/constants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock, ArrowUpRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactInfo = [
  { icon: MapPin, label: 'Office', value: BUSINESS.address },
  { icon: Phone, label: 'Phone', value: BUSINESS.phone, href: `tel:${BUSINESS.phone}` },
  { icon: Mail, label: 'Email', value: BUSINESS.email, href: `mailto:${BUSINESS.email}` },
  { icon: Clock, label: 'Hours', value: 'Monday - Friday, 9:00 AM - 6:00 PM PST' },
];

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '', message: '', inquiry_type: 'general'
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('Thank you! We will be in touch shortly.');
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '', company: '', message: '', inquiry_type: 'general' });
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="bg-wc-charcoal py-24 lg:py-32" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-4">Contact Us</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white max-w-3xl leading-[1.1]">
              Let's Talk About Your Business
            </h1>
            <p className="font-body text-base lg:text-lg text-white/55 mt-6 max-w-2xl leading-relaxed">
              Whether you need bookkeeping support, have questions about our services, or are ready to schedule a consultation, we're here to help.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* CONTACT CONTENT */}
      <section className="py-24 lg:py-32 bg-white" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-16 lg:gap-20">
            {/* INFO */}
            <div className="lg:col-span-2">
              <FadeIn>
                <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-6">Get in Touch</p>
                <h2 className="font-heading text-3xl font-light tracking-tight text-wc-black mb-10">
                  {BUSINESS.name}
                </h2>
                <div className="space-y-8">
                  {contactInfo.map((info, i) => (
                    <div key={i} className="flex gap-4">
                      <info.icon className="w-5 h-5 text-wc-gold flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <div>
                        <p className="font-accent text-xs uppercase tracking-[0.15em] text-wc-muted mb-1">{info.label}</p>
                        {info.href ? (
                          <a
                            href={info.href}
                            className="font-body text-sm text-wc-black hover:text-wc-gold transition-colors duration-300 break-all"
                            data-testid={`contact-info-${info.label.toLowerCase()}`}
                          >
                            {info.value}
                          </a>
                        ) : (
                          <p className="font-body text-sm text-wc-black" data-testid={`contact-info-${info.label.toLowerCase()}`}>{info.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="w-12 h-px bg-wc-gold mt-10 mb-8" />
                <p className="font-body text-sm text-wc-muted leading-relaxed">
                  We respond to all inquiries within one business day. For urgent matters, please call us directly.
                </p>
              </FadeIn>
            </div>

            {/* FORM */}
            <div className="lg:col-span-3">
              <FadeIn delay={150}>
                {submitted ? (
                  <div className="border border-wc-border p-12 text-center" data-testid="contact-success">
                    <div className="w-12 h-px bg-wc-gold mx-auto mb-6" />
                    <h3 className="font-heading text-2xl font-normal text-wc-black mb-4">Thank You</h3>
                    <p className="font-body text-base text-wc-muted leading-relaxed mb-8">
                      Your inquiry has been received. We will review your message and respond within one business day.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="font-body text-sm uppercase tracking-[0.1em] text-wc-black border-b border-wc-black pb-0.5 hover:border-wc-gold hover:text-wc-gold transition-colors duration-300"
                      data-testid="contact-send-another"
                    >
                      Send Another Inquiry
                    </button>
                  </div>
                ) : (
                  <div className="border border-wc-border p-8 lg:p-12" data-testid="contact-form-container">
                    <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-2">Inquiry Form</p>
                    <h3 className="font-heading text-2xl font-normal text-wc-black mb-8">Send Us a Message</h3>
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <Label className="font-body text-xs uppercase tracking-[0.1em] text-wc-muted">Full Name *</Label>
                          <Input
                            value={form.name}
                            onChange={(e) => update('name', e.target.value)}
                            placeholder="Your full name"
                            className="rounded-none border-wc-border font-body text-sm h-11 focus-visible:ring-wc-gold"
                            data-testid="contact-name"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-body text-xs uppercase tracking-[0.1em] text-wc-muted">Email Address *</Label>
                          <Input
                            type="email"
                            value={form.email}
                            onChange={(e) => update('email', e.target.value)}
                            placeholder="your@email.com"
                            className="rounded-none border-wc-border font-body text-sm h-11 focus-visible:ring-wc-gold"
                            data-testid="contact-email"
                            required
                          />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div className="space-y-2">
                          <Label className="font-body text-xs uppercase tracking-[0.1em] text-wc-muted">Phone</Label>
                          <Input
                            value={form.phone}
                            onChange={(e) => update('phone', e.target.value)}
                            placeholder="(555) 000-0000"
                            className="rounded-none border-wc-border font-body text-sm h-11 focus-visible:ring-wc-gold"
                            data-testid="contact-phone"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-body text-xs uppercase tracking-[0.1em] text-wc-muted">Company / Business</Label>
                          <Input
                            value={form.company}
                            onChange={(e) => update('company', e.target.value)}
                            placeholder="Your business name"
                            className="rounded-none border-wc-border font-body text-sm h-11 focus-visible:ring-wc-gold"
                            data-testid="contact-company"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="font-body text-xs uppercase tracking-[0.1em] text-wc-muted">Inquiry Type</Label>
                        <Select value={form.inquiry_type} onValueChange={(v) => update('inquiry_type', v)}>
                          <SelectTrigger className="rounded-none border-wc-border font-body text-sm h-11" data-testid="contact-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-none">
                            <SelectItem value="general">General Inquiry</SelectItem>
                            <SelectItem value="consultation">Free 15-Minute Consultation</SelectItem>
                            <SelectItem value="cleanup">$200 Book Cleanup</SelectItem>
                            <SelectItem value="bookkeeping">Bookkeeping</SelectItem>
                            <SelectItem value="wine">Vintage Wine & Champagne</SelectItem>
                            <SelectItem value="webdesign">Web Design</SelectItem>
                            <SelectItem value="events">Events & Vendor Connections</SelectItem>
                            <SelectItem value="it">IT & Programming</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="font-body text-xs uppercase tracking-[0.1em] text-wc-muted">Message *</Label>
                        <Textarea
                          value={form.message}
                          onChange={(e) => update('message', e.target.value)}
                          placeholder="Tell us about your business and how we can help. Include any details about your current bookkeeping situation, business size, or specific needs."
                          rows={5}
                          className="rounded-none border-wc-border font-body text-sm focus-visible:ring-wc-gold resize-none"
                          data-testid="contact-message"
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-wc-black text-white py-3.5 font-body text-xs uppercase tracking-[0.15em] hover:bg-wc-charcoal transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2 group"
                        data-testid="contact-submit"
                      >
                        {loading ? 'Submitting...' : (
                          <>
                            Submit Inquiry
                            <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                )}
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* RESPONSE TIME NOTE */}
      <section className="py-20 bg-wc-bg-alt border-t border-wc-border" data-testid="contact-location">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <FadeIn>
            <div className="text-center">
              <p className="font-accent text-xs uppercase tracking-[0.2em] text-wc-gold mb-2">Beverly Hills Office</p>
              <p className="font-heading text-2xl font-light text-wc-black">{BUSINESS.address}</p>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
